import { useState, useEffect, useRef } from 'react'
import { parseCookies } from 'nookies'
import _ from 'lodash'

const BookingsStream = ({ id, onStatusChange, onConnectionChange, onNewBooking, url }) => {
  const [sseConnected, setSseConnected] = useState(false)
  const { accessToken } = parseCookies()
  const token = _.split(accessToken, ' ')[1]
  const sseRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)

  const onStatusChangeRef = useRef(onStatusChange)
  const onNewBookingRef = useRef(onNewBooking)
  const onConnectionChangeRef = useRef(onConnectionChange)
  const idRef = useRef(id)

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange
  }, [onStatusChange])

  useEffect(() => {
    onNewBookingRef.current = onNewBooking
  }, [onNewBooking])

  useEffect(() => {
    onConnectionChangeRef.current = onConnectionChange
  }, [onConnectionChange])

  useEffect(() => {
    idRef.current = id
  }, [id])

  useEffect(() => {
    if (onConnectionChangeRef.current) {
      onConnectionChangeRef.current(sseConnected)
    }
  }, [sseConnected])

  useEffect(() => {
    if (!url || !token) return

    const connectSSE = () => {
      if (sseRef.current) {
        sseRef.current.close()
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }

      const apiUrl = `${url}?token=${encodeURIComponent(token)}`
      const eventSource = new EventSource(apiUrl)
      sseRef.current = eventSource

      eventSource.onopen = () => {
        setSseConnected(true)
        reconnectAttemptsRef.current = 0
        console.log('SSE connection established')
      }

      eventSource.onmessage = event => {
        try {
          const data = JSON.parse(event.data)
          const currentId = idRef.current

          if (data.connected) {
            console.log('SSE connection confirmed')
            return
          }

          // eslint-disable-next-line
          if (Array.isArray(data)) {
            // eslint-disable-next-line
            data.forEach(booking => {
              const bookingId = booking._id || booking.id
              const { status } = booking

              if (bookingId && status) {
                if (!currentId || bookingId === currentId) {
                  onStatusChangeRef.current?.(bookingId, status, booking)
                }

                if (onNewBookingRef.current) {
                  onNewBookingRef.current(booking)
                }
              }
            })
          } else if (data.bookingId && data.status) {
            if (!currentId || data.bookingId === currentId) {
              onStatusChangeRef.current?.(data.bookingId, data.status, data)

              if (onNewBookingRef.current) {
                onNewBookingRef.current(data)
              }
            }
          }
        } catch (error) {
          console.error('Error parsing SSE data:', error)
        }
      }

      eventSource.onerror = error => {
        console.error('SSE Error:', error)
        setSseConnected(false)

        if (sseRef.current) {
          sseRef.current.close()
          sseRef.current = null
        }

        const maxAttempts = 5
        if (reconnectAttemptsRef.current < maxAttempts) {
          const delay = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 30000)
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxAttempts})`)

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current += 1
            connectSSE()
          }, delay)
        } else {
          console.error('Max reconnection attempts reached')
        }
      }
    }

    connectSSE()

    return () => {
      if (sseRef.current) {
        sseRef.current.close()
        sseRef.current = null
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      setSseConnected(false)
    }
  }, [url, token])

  return null
}

export default BookingsStream
